import { Map as LeafletMap } from 'leaflet';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Esri from 'esri-leaflet';

import {
  esriDemographicsServer,
  endpoints,
  esriDemographicZoomLayers,
} from '../../../config';
import { extractLabeledColor, palette } from '../../../utils/colors';
import { humanizeNumber } from '../../../utils/format';

import { activateMapLoadingStatus, deactivateMapLoadingStatus } from '../../../store/actions';

export function getLayerColor(value, colors) {
  const layerColor = colors.find(([threshold]) => {
    return value < threshold;
  }) || colors[colors.length - 1];
  const color = layerColor[1];
  return color;
}

export function setLayer(layerValue) {
  return (...args) => {
    // for the onEachFeature method the layer is the second argument
    // for the eachFeature method, the layer is the first argumen
    const layer = args[1] || args[0];
    const value = layer.feature.properties[layerValue.name];
    layer.setStyle({
      weight: 1,
      fillOpacity: 0.6,
      opacity: 0.5,
      color: '#000000',
      fillColor: getLayerColor(value, layerValue.colors),
    });
    layer.bindTooltip(`<div><b>${layerValue.label}:</b> ${humanizeNumber(value)}</div>`, { sticky: true });
  };
}

export class DemographicsLayerComponent extends Component {

  static contextTypes = Component.contextTypes;
  static childContextTypes = Component.childContextTypes;
  static contextTypes = {
    map: PropTypes.instanceOf(LeafletMap),
  };

  constructor(props) {
    super(props);
    this.state = { feature: null };
    this.esriValues = {
      population: {
        name: 'POPDENS_CY',
        label: 'Population / Sq Mi',
        colors: [
          10,
          50,
          100,
          500,
          750,
          1000,
          1001,
        ].map(extractLabeledColor(palette.variant1)),
      },
      diversity: {
        name: 'DIVINDX_CY',
        label: 'Diversity Index',
        colors: [
          10,
          20,
          40,
          60,
          80,
          90,
          91,
        ].map(extractLabeledColor(palette.variant2)),
      },
      migration: {
        name: 'POPGRW10CY',
        label: 'Migration',
        colors: [
          1,
          5,
          10,
          20,
          40,
          60,
          61,
        ].map(extractLabeledColor(palette.variant3)),
      },
      households: {
        name: 'TOTHH_CY',
        label: 'Total Households',
        colors: [
          50,
          300,
          600,
          900,
          1600,
          2500,
          2501,
        ].map(extractLabeledColor(palette.variant4)),
      },
      employed: {
        name: 'CIVLBFR_CY',
        label: 'Civilian - Employed (%)',
        colors: [
          50,
          55,
          65,
          75,
          85,
          95,
          96,
        ].map(extractLabeledColor(palette.variant5)),
      },
      home: {
        name: 'MEDVAL_CY',
        label: 'Median Home Value',
        colors: [
          75000,
          100000,
          200000,
          300000,
          500000,
          1000000,
          1000001,
        ].map(extractLabeledColor(palette.variant6)),
      },
      income: {
        name: 'MEDHINC_CY',
        label: 'Median Household Income',
        colors: [
          35000,
          50000,
          75000,
          100000,
          150000,
          200000,
          200001,
        ].map(extractLabeledColor(palette.variant7)),
      },
    };
  }

  componentDidMount() {
    const env = process.env.REACT_APP_API || 'local';

    const { startLoading, stopLoading } = this.props;

    const { demographicsUrl, demographicsLayersEndPoint } = endpoints[env];

    const proxy = demographicsUrl + demographicsLayersEndPoint;

    const defaultFeatureConfig = {
      url: `${esriDemographicsServer}/25`,
      proxy,
      fields: ['OBJECTID', 'POPDENS_CY', 'MEDHINC_CY', 'DIVINDX_CY', 'POPGRW10CY', 'TOTHH_CY', 'CIVLBFR_CY', 'MEDVAL_CY'],
    };

    startLoading();

    const features = esriDemographicZoomLayers.map(config => (
      Esri.featureLayer({
        ...defaultFeatureConfig,
        ...config,
        url: `${esriDemographicsServer}/${config.layer}`,
        onEachFeature: (...args) => {
          return setLayer(this.esriValues[this.props.demographics])(...args);
        },
      })
    ));

    Promise.race(features.map(feature => {
      feature.addTo(this.context.map);

      return new Promise(resolve => {
        feature.once('load', resolve);
      });
    }))
      .then(() => {
        this.setState({ features });
        stopLoading();
      })
    ;
  }

  shouldComponentUpdate(previous) {
    if (previous.demographics !== this.props.demographics) {
      return true;
    }
    return false;
  }

  componentDidUpdate(previous) {
    if (previous.demographics !== this.props.demographics) {
      const features = this.state.features || [];
      const layerValue = this.esriValues[this.props.demographics];
      features.forEach(feature => {
        if (feature && feature.eachFeature) {
          feature.eachFeature(setLayer(layerValue));
        }
      });
    }
  }

  componentWillUnmount() {
    const features = this.state.features || [];
    features.forEach(feature => {
      if (feature && feature.remove && feature._map) {
        feature.remove();
      }
    });
  }

  render() {
    return null;
  }
}

DemographicsLayerComponent.contextTypes = {
  map: PropTypes.instanceOf(LeafletMap),
};

DemographicsLayerComponent.propTypes = {
  demographics: PropTypes.string,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
};

const mapDispatchToProps = dispatch => ({
  startLoading: () => dispatch(activateMapLoadingStatus()),
  stopLoading: () => dispatch(deactivateMapLoadingStatus()),
});

export default connect(null, mapDispatchToProps)(DemographicsLayerComponent);



// WEBPACK FOOTER //
// ./src/components/Map/Layers/Demographics.js